import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useLDClient } from "launchdarkly-react-client-sdk"
import type { LDFlags, ChangedLDFlags } from "types-lib/launchDarkly"
import type { LDClient } from "launchdarkly-js-client-sdk"

/**
 * Custom hook to manage feature flags using LaunchDarkly.
 *
 * @template T - The type of the flags object. Defaults to LDFlags.
 * @param {string[]} flags - An array of feature flag keys to be managed.
 * @returns {T} An object containing the current values of the specified feature flags.
 *
 * This hook initializes the local state with the values of the specified feature flags from the LaunchDarkly client.
 * It then updates the local state whenever the flags change, ensuring that the local state is the source of truth
 * for the component.
 *
 * The feature flag keys are formatted to follow the pattern `LD_FF_myFlagName` for easier identification and removal.
 *
 * @example
 * const flags = useFeatureFlags(['flag1', 'flag2']);
 * console.log(flags.flag1); // Output: true or false
 */
const useFeatureFlags = <T = LDFlags,>(flags: string[]): T => {
  const [localFlags, setLocalFlags] = useState<T>({} as T)
  const isLDClientReady = useRef(false)
  const isLDClientErrored = useRef(false)

  const ldClient: LDClient | undefined = useLDClient()

  const fetchAllFlags = useCallback(
    () =>
      flags.reduce((acc: Partial<T>, flag) => {
        acc[flag as keyof T] = ldClient?.variation(
          flag,
          false,
        ) as unknown as T[keyof T]
        return acc
      }, {} as Partial<T>) as T,
    [ldClient, flags],
  )

  const fetchChangedFlags = useCallback(
    (changes: ChangedLDFlags) =>
      Object.keys(changes as object).reduce((acc: Partial<T>, flag) => {
        acc[flag as keyof T] = changes?.[flag]?.current as T[keyof T]
        return acc
      }, {} as Partial<T>) as T,
    [],
  )

  const initializeLDClient = useCallback(async () => {
    if (ldClient) {
      try {
        await ldClient.waitForInitialization(5) // Add a 5 second timeout while waiting for the ldClient to initialize, this cleans up annoying warnings. Future versions of the aunchdarkly-react-client-sdk will add a default timeout of 5 seconds.
        isLDClientReady.current = true
      } catch (error) {
        console.error("Error during LaunchDarkly initialization:", error)
        isLDClientErrored.current = true
      }
    }
  }, [ldClient])

  const handleFlagChange = useCallback(
    (changes: ChangedLDFlags) => {
      // check if the changes are in the flags passed to the hook, if not, skip the update
      const isFlagChanged = Object.keys(changes as object).some((flag) =>
        flags.includes(flag),
      )
      if (!isFlagChanged) {
        return
      }

      if (ldClient) {
        const updatedFlags = fetchChangedFlags(changes)
        setLocalFlags(updatedFlags)
      }
    },
    [ldClient, flags, fetchChangedFlags],
  )

  useEffect(() => {
    const isLDReady = isLDClientReady.current

    if (ldClient && !isLDReady) {
      initializeLDClient()
        .then(() => {
          const updatedFlags = fetchAllFlags()
          setLocalFlags(updatedFlags)
        })
        .catch((error) => {
          console.error("Error initializing LaunchDarkly client:", error)
          if (!isLDClientErrored.current) {
            isLDClientErrored.current = true
          }
        })
    }
  }, [initializeLDClient, ldClient, fetchAllFlags])

  useEffect(() => {
    if (ldClient) {
      ldClient.on("change", handleFlagChange)
    }

    return () => {
      if (ldClient) {
        ldClient.off("change", handleFlagChange)
      }
    }
  }, [ldClient, handleFlagChange])

  const memoizedFlags = useMemo(
    () => ({
      ...localFlags,
      isLDClientReady: isLDClientReady.current,
      isLDClientErrored: isLDClientErrored.current,
    }),
    [localFlags],
  )

  return memoizedFlags
}

export default useFeatureFlags
